import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { formatDistanceToNow } from "date-fns"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { navigate } from "lib/routes/navigate"
import {
    getShareLinkCommentOpenSchedulePath,
    getShareLinkCommentRouteCommentShareLinkId,
    getShareLinkCommentRouteShareLinkId,
    getShareLinkCommentScheduleRoute,
} from "routes/schedule/plugins/share-link-comments/share-link-comment-presenters"
import { CommentScheduleAvatarStack } from "./comment-schedule-avatar-stack"

const EMPTY_THREAD_IDS = []

// One schedule's comments rolled up into a single clickable card for the regime Comments directory:
// the schedule title + code + version, a count of every comment left on it, the distinct commenters as
// stacked avatars, and the most recent activity time. The whole card is a navigation control — clicking
// jumps to the schedule's Comments tab where the threads are read and replied to. This is a routing
// surface only; status/recipient/schedule filtering still happens in the toolbar above.
export function ShareLinkCommentScheduleCard({ group }) {
    const { markThreadSeen, unreadThreadSet } = useBoundContext()
    const [currentShareLinkId] = useSearchParam("share", null)
    const [currentVersion] = useSearchParam("version", null)
    if (!group) return null

    const comment = group.comment ?? {}
    const threadIds = group.threadIds ?? EMPTY_THREAD_IDS
    const unreadCount = threadIds.filter((id) => unreadThreadSet?.has(id)).length
    const isUnread = unreadCount > 0
    const openRoute = getOpenRoute()

    return (
        <Box
            data-cy={`share-link-comment-schedule-card-${group.scheduleId ?? group.id}`}
            data-unread={getUnreadAttribute()}
            onClick={openSchedule}
            sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                width: 1,
            }}
        >
            <ListItemBox
                data-cy="share-link-comment-schedule-row"
                sx={{
                    gap: 2,
                    py: 1,
                    px: 1.5,
                    cursor: getCursor(),
                    borderRadius: 1,
                    "&:hover": getHoverSx(),
                }}
            >
                <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                    <ListItemBox sx={{ gap: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" noWrap sx={{ minWidth: 0 }}>
                            {getTitle()}
                        </Typography>
                        <If truthy={!!group.scheduleCode}>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {group.scheduleCode}
                            </Typography>
                        </If>
                        <If truthy={!!getVersionLabel()}>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                · {getVersionLabel()}
                            </Typography>
                        </If>
                        <If truthy={!!getRegimeVersionLabel()}>
                            <Box
                                component="span"
                                data-cy="comment-schedule-regime-version"
                                sx={{
                                    flexShrink: 0,
                                    height: 18,
                                    px: 0.75,
                                    borderRadius: 999,
                                    bgcolor: "primary.lighter",
                                    color: "primary.darker",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    lineHeight: "18px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {getRegimeVersionLabel()}
                            </Box>
                        </If>
                    </ListItemBox>
                    <If truthy={!!group.scheduleLocation}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            data-cy="comment-schedule-location"
                            sx={{ minWidth: 0 }}
                        >
                            {group.scheduleLocation}
                        </Typography>
                    </If>
                    <ListItemBox sx={{ gap: 0.5, color: "text.secondary" }}>
                        <Iconify icon="mdi:comment-text-outline" sx={{ width: 16, height: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                            {getCommentCountLabel()}
                        </Typography>
                        <If truthy={getResolvedCount() > 0}>
                            <Iconify
                                icon="line-md:circle-to-confirm-circle-transition"
                                sx={{ width: 16, height: 16, ml: 0.5 }}
                            />
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                data-cy="comment-schedule-resolved-count"
                            >
                                {getResolvedCount()} Resolved
                            </Typography>
                        </If>
                    </ListItemBox>
                </Stack>
                <CommentScheduleAvatarStack commenters={group.commenters} />
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                    {getRelativeTime()}
                </Typography>
                <If value={isUnread}>
                    <Box
                        component="span"
                        data-cy="comment-schedule-unread-marker"
                        sx={{
                            flexShrink: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            height: 18,
                            px: 0.75,
                            borderRadius: 999,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: 0.4,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {unreadCount} unread
                    </Box>
                </If>
                <If truthy={!!getDraftRoute()}>
                    <Tooltip arrow title="Edit in the draft regime">
                        <IconButton
                            size="small"
                            onClick={openDraft}
                            data-cy="share-link-comment-schedule-edit-draft"
                            aria-label="Edit in the draft regime"
                            sx={{ flexShrink: 0 }}
                        >
                            <Iconify icon="mdi:pencil-outline" sx={{ width: 18, height: 18 }} />
                        </IconButton>
                    </Tooltip>
                </If>
                <Iconify
                    icon="eva:arrow-ios-forward-fill"
                    sx={{ flexShrink: 0, color: "text.secondary", width: 20, height: 20 }}
                />
            </ListItemBox>
        </Box>
    )

    function getUnreadAttribute() {
        if (isUnread) return "true"
        return undefined
    }

    function getCursor() {
        if (openRoute) return "pointer"
        return "default"
    }

    function getHoverSx() {
        if (openRoute) return { bgcolor: "action.hover" }
        return undefined
    }

    function openSchedule() {
        if (!openRoute) return
        markThreadsSeen()
        navigate(openRoute)
    }

    // The card's main click lands on the live/candidate version the comments were written against (a
    // faithful, read-only context). Editing happens in the draft, which may have diverged via
    // tailoring/customisation — so it's a separate, explicit action. Stop propagation so the draft jump
    // doesn't also fire the card's read route.
    function openDraft(event) {
        event.stopPropagation()
        const route = getDraftRoute()
        if (!route) return
        markThreadsSeen()
        navigate(route)
    }

    // The draft is the editable copy of the regime, so no version pin. Carry the comment share link so
    // the draft comments panel can resolve the exact published regime version the thread belongs to.
    // Open it in tailoring mode with the Comments right bar still selected so the reviewer can edit and
    // resolve the selected thread in one place. Null when no draft.
    function getDraftRoute() {
        if (!group.draftSchedulePath) return null
        return getShareLinkCommentScheduleRoute({
            schedulePath: group.draftSchedulePath,
            commentShareLinkId: group.shareLinkId,
            commentId: comment.id,
            editEx: true,
        })
    }

    // Opening the schedule is where the threads are actually read, so mark every unread thread on this
    // schedule seen — the tracker batches them into one mark-read call and clears the tab badge.
    function markThreadsSeen() {
        if (!markThreadSeen) return
        for (const id of threadIds) {
            if (unreadThreadSet?.has(id)) markThreadSeen(id)
        }
    }

    // The schedule's Comments-tab route, or null when no schedule path resolves. Use the path stored
    // on the comment so the default click opens the schedule version/location the comment was made on.
    function getOpenRoute() {
        const schedulePath = getShareLinkCommentOpenSchedulePath(comment)
        if (!schedulePath) return null
        return getShareLinkCommentScheduleRoute({
            schedulePath,
            shareLinkId: getShareLinkCommentRouteShareLinkId({ currentShareLinkId, shareLinkId: group.shareLinkId }),
            commentShareLinkId: getShareLinkCommentRouteCommentShareLinkId({
                currentShareLinkId,
                shareLinkId: group.shareLinkId,
            }),
            commentId: comment.id,
            version: getOpenVersion(),
        })
    }

    function getOpenVersion() {
        const version = group.scheduleVersion
        if (version !== undefined && version !== null && version !== "") return version
        return currentVersion
    }

    function getTitle() {
        return group.scheduleTitle ?? "Schedule"
    }

    function getVersionLabel() {
        const version = Number.parseInt(group.scheduleVersion, 10)
        if (Number.isNaN(version)) return ""
        return `V${version}`
    }

    function getRegimeVersionLabel() {
        return group.regimeVersion?.trim() || ""
    }

    function getCommentCountLabel() {
        const count = group.commentCount ?? 0
        return `${count} ${count === 1 ? "Comment" : "Comments"}`
    }

    function getResolvedCount() {
        return group.resolvedCount ?? 0
    }

    function getRelativeTime() {
        const date = new Date(group.updated)
        if (Number.isNaN(date.getTime())) return ""
        try {
            return formatDistanceToNow(date, { addSuffix: true })
        } catch {
            return date.toLocaleString()
        }
    }
}
